import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import { NavIcons } from "./ui/Icons";

// =============================================================================
// MOBILE NAV SHEET — swipe-up / tap-handle bottom sheet, 3-column all-pages grid
// =============================================================================
// PURPOSE: Replaces MobileMoreDrawer. Opened via the grab-handle strip or a
// swipe-up gesture on MobileBottomNav. Shows every accessible page (not just
// the overflow past the primary tabs) in a 3-column grid so the whole app is
// one tap away on mobile.
//
// Structurally mirrors components/calendar/CalendarSidebarSheet.tsx (portal +
// ESC-to-close + role="dialog"), swapped to a light frosted backdrop per
// Mako's ask — bg-black/50 darkened the whole screen and looked bad. Blur
// does the visual separation now, not darkness.
// =============================================================================

interface MobileNavSheetItem {
  path: string;
  label: string;
  iconKey: string;
}

interface MobileNavSheetProps {
  isOpen: boolean;
  onClose: () => void;
  items: MobileNavSheetItem[];
}

const SWIPE_DOWN_CLOSE_DY = 40;

const MobileNavSheet: React.FC<MobileNavSheetProps> = ({ isOpen, onClose, items }) => {
  const touchStartYRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Resolve icon component from iconKey string
  const getIcon = (iconKey: string, className: string) => {
    const IconComponent = NavIcons[iconKey as keyof typeof NavIcons];
    if (!IconComponent) return null;
    return <IconComponent className={className} />;
  };

  // Swipe-down-to-close on the header region (handle + title row) only — the
  // scrollable grid below needs its own vertical gesture for scrolling.
  const handleHeaderTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleHeaderTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartYRef.current === null) return;
    const dy = e.touches[0].clientY - touchStartYRef.current;
    if (dy > SWIPE_DOWN_CLOSE_DY) {
      touchStartYRef.current = null;
      onClose();
    }
  };

  const handleHeaderTouchEnd = () => {
    touchStartYRef.current = null;
  };

  return createPortal(
    <div
      className="lg:hidden fixed inset-0 z-[9999] flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-nav-sheet-title"
    >
      {/* Backdrop — light frosted blur, NOT a black scrim. Screen stays light;
          blur does the separation work instead of darkness (Mako's ask). */}
      <div
        className="anim-backdrop-in absolute inset-0 bg-black/10 backdrop-blur-md"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        data-testid="mobile-nav-sheet"
        className="anim-sheet-in detail-overlay relative w-full max-h-[80vh] rounded-t-2xl border border-black/[0.08] shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex-shrink-0"
          onTouchStart={handleHeaderTouchStart}
          onTouchMove={handleHeaderTouchMove}
          onTouchEnd={handleHeaderTouchEnd}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-black/[0.15] rounded-full" />
          </div>

          {/* Header */}
          <div className="px-4 pb-3 flex justify-between items-center">
            <h3 id="mobile-nav-sheet-title" className="token-14 font-semibold text-glass">
              All Pages
            </h3>
            <button
              onClick={onClose}
              className="anim-tap p-2 -mr-2 text-glass-muted hover:text-glass-secondary transition-colors"
              aria-label="Close"
            >
              <NavIcons.Close className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Page grid — overflow lives on this inner element, not the blurred
            panel, so momentum scroll + any future sticky descendants work. */}
        <nav className="grid grid-cols-3 gap-2 p-3 overflow-y-auto">
          {items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1.5 py-4 px-2 rounded-xl transition-colors ${
                  isActive
                    ? "bg-blue-500/15 text-blue-400"
                    : "text-glass-secondary active:bg-black/[0.06]"
                }`
              }
            >
              {getIcon(item.iconKey, "w-6 h-6")}
              <span className="token-11 font-medium text-center leading-tight line-clamp-2">
                {item.label}
              </span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>,
    document.body
  );
};

export default MobileNavSheet;
